import { useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import type { ApiError, AssignmentListItem, AssignmentStatus } from '@/types/api';
import { useCompleteAssignment } from '@/hooks/use-assignments';
import { haptic, openBotChat } from '@/lib/telegram';
import { formatRelative } from '@/lib/format';
import { cls } from '@/lib/format';

interface Props {
  assignment: AssignmentListItem;
  onClose: () => void;
}

// Human-readable labels for status pills. Keep in sync with the
// AssignmentStatus union in types/api.ts.
const STATUS_LABELS: Record<AssignmentStatus, string> = {
  pending: 'Pending',
  awaiting_approval: 'Awaiting approval',
  completed: 'Completed',
  expired: 'Expired',
};

function statusPillClasses(status: AssignmentStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-green-500/20 text-green-600 dark:text-green-400';
    case 'awaiting_approval':
      return 'bg-amber-500/20 text-amber-600 dark:text-amber-400';
    case 'expired':
      return 'bg-red-500/15 text-[var(--color-destructive)]';
    default:
      return 'bg-[var(--color-button)]/15 text-[var(--color-accent)]';
  }
}

export function AssignmentDetailSheet({ assignment, onClose }: Props) {
  const complete = useCompleteAssignment();

  // Lock body scroll while the sheet is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Fall back to 'none' for both null/undefined *and* empty string — older
  // backend builds shipped an empty report_mode before the JOIN was fixed.
  const rawReportMode = assignment.practice_seed?.report_mode;
  const reportMode = rawReportMode && rawReportMode.length > 0 ? rawReportMode : 'none';
  // Only seeds with report_mode=required block in-app completion. Optional
  // seeds let the student "skip the report" (same semantic as the bot's
  // ⏭ Skip report button) and complete directly.
  const reportRequired = reportMode === 'required';
  const reportOptional = reportMode === 'optional';
  const isPending = assignment.status === 'pending';

  const handleComplete = async () => {
    try {
      await complete.mutateAsync(assignment.id);
      haptic('success');
      toast.success('Marked as done');
      onClose();
    } catch (err) {
      haptic('error');
      // Surface the backend's report_required guard explicitly — it fires
      // when an admin flipped report_mode → required after the list was
      // fetched but before the student tapped Done.
      if (axios.isAxiosError(err)) {
        const body = err.response?.data as ApiError | undefined;
        if (body?.error?.code === 'REPORT_REQUIRED') {
          toast.error('This practice needs a report — please use the bot.');
          return;
        }
      }
      toast.error('Could not mark as done');
    }
  };

  const handleOpenBot = () => {
    haptic('light');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const botUsername = (window as any).__BOT_USERNAME__ ?? '';
    if (botUsername) openBotChat(botUsername);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-30 flex items-end bg-black/50" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl bg-[var(--color-bg)] px-4 pb-6 pt-4 shadow-2xl"
        style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 24px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--color-separator)]" />

        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">
            {assignment.practice_seed?.title ?? '(untitled)'}
          </h2>
          <span
            className={cls(
              'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
              statusPillClasses(assignment.status),
            )}
          >
            {STATUS_LABELS[assignment.status]}
          </span>
        </div>

        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Assigned {formatRelative(assignment.assigned_at)}
          {assignment.due_date ? ` · due ${formatRelative(assignment.due_date)}` : ''}
        </p>
        {assignment.completed_at ? (
          <p className="mt-0.5 text-sm text-[var(--color-muted)]">
            Completed {formatRelative(assignment.completed_at)}
          </p>
        ) : null}

        {assignment.practice_seed?.description ? (
          <p className="mt-3 whitespace-pre-wrap text-sm text-[var(--color-fg)]">
            {assignment.practice_seed.description}
          </p>
        ) : null}

        {/* Action area — only rendered for pending assignments. Non-pending
            items (completed, awaiting_approval, expired) show a read-only
            detail view with just a Close button. */}
        {isPending ? (
          reportRequired ? (
            <div className="mt-4 rounded-xl bg-[var(--color-secondary-bg)] px-4 py-3 text-sm">
              <p className="font-medium text-[var(--color-fg)]">
                Your teacher requires evidence.
              </p>
              <p className="mt-1 text-[var(--color-muted)]">
                Open the bot chat to send a photo, video, or note. Your teacher
                will review and approve it before the practice is marked done.
              </p>
              <button
                type="button"
                onClick={handleOpenBot}
                className="mt-3 w-full rounded-xl bg-[var(--color-button)] py-3 font-semibold text-[var(--color-button-fg)]"
              >
                Open bot
              </button>
            </div>
          ) : (
            <>
              {reportOptional ? (
                <div className="mt-4 rounded-xl bg-[var(--color-secondary-bg)] px-4 py-3 text-sm">
                  <p className="text-[var(--color-muted)]">
                    Your teacher welcomes a report. Attach a photo, video, or
                    note in the bot — or skip and just mark it done below.
                  </p>
                  <button
                    type="button"
                    onClick={handleOpenBot}
                    className="mt-3 w-full rounded-xl bg-[var(--color-secondary-bg)] py-2 text-sm font-medium text-[var(--color-accent)]"
                  >
                    Attach report in bot →
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={handleComplete}
                disabled={complete.isPending}
                className="mt-4 w-full rounded-xl bg-[var(--color-button)] py-3 font-semibold text-[var(--color-button-fg)] disabled:opacity-60"
              >
                {complete.isPending ? 'Marking…' : 'Mark as done'}
              </button>
            </>
          )
        ) : assignment.status === 'awaiting_approval' ? (
          <div className="mt-4 rounded-xl bg-[var(--color-secondary-bg)] px-4 py-3 text-sm text-[var(--color-muted)]">
            Your report is waiting for your teacher's approval. You'll get a
            message in the bot when they review it.
          </div>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-xl bg-[var(--color-secondary-bg)] py-3 font-medium text-[var(--color-fg)]"
        >
          Close
        </button>
      </div>
    </div>
  );
}
